import moment from "moment";
import {
  BillingFilters,
  ClientAddon,
  ClientBilling,
  InvoiceStatus,
  PlanInfoMap,
  STATUS_PRIORITY,
  SummaryStats,
} from "./types";

/**
 * A client can be charged even with $0 commission when there's something else to
 * bill: a platform fee (hybrid/fixed), an enabled add-on with a price, or a
 * performance-backed minimum fee (commission_or_flat). The backend charge path
 * already bills each of these on its own, so this gates the admin UI — a
 * commission-free client with something billable must not be blocked from
 * charging.
 *
 * The minimum-fee clause reads the backend's `minimumFeeMayApply`, never the
 * plan fields alone. Enrolment is not enough: while the minimum is switched off
 * platform-wide (PERFORMANCE_MINIMUM_ENABLED), or before the client's first full
 * calendar month on the plan, a $0-commission month bills $0, and offering the
 * charge would make the grid read it as chargeable. The flag is still
 * permissive about the one gate the backend can't evaluate without GA4, the
 * engaged-session share, because a $0-commission month topped up to the minimum
 * is precisely the case the plan exists for. The charge preview stays the
 * authoritative pre-charge answer. A list response without the flag reads as
 * false, so a stale backend can never re-open the button.
 */
export const isChargeableWithoutCommission = (
  client: ClientBilling,
): boolean => {
  const hasPlatformFee =
    (client.billingType === "hybrid" || client.billingType === "fixed") &&
    (client.platformFeeAmount ?? 0) > 0;
  const hasBillableAddon = (client.addons ?? []).some(
    (a) => a.enabled && (a.monthlyPrice ?? 0) > 0,
  );
  const hasMinimumFee = client.minimumFeeMayApply === true;
  return hasPlatformFee || hasBillableAddon || hasMinimumFee;
};

// =============================================================================
// ADD-ON NORMALIZATION
// =============================================================================

// The backend sends decimal prices as JSON numbers, but historical/enrichment
// payloads can send them as strings — normalize to a number-or-null here so the
// rest of the UI never has to parse.
const mapAddons = (raw: unknown): ClientAddon[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map((addon: any) => ({
    type: addon.type,
    displayName: addon.displayName,
    enabled: addon.enabled ?? false,
    monthlyPrice:
      addon.monthlyPrice != null ? parseFloat(String(addon.monthlyPrice)) : null,
    startDate: addon.startDate ?? null,
    trialEnabled: addon.trialEnabled ?? false,
    trialEndDate: addon.trialEndDate ?? null,
  }));
};

// =============================================================================
// HOTEL-NAME REWRITE FOR STRIPE PLAN DESCRIPTIONS
// =============================================================================

// Stripe Product descriptions are sometimes shared across customers (template/
// reuse), so a row's planDescription can list other clients' hotel names. We
// can't fix Stripe from here, but we can swap any known hotel-name run in the
// description with the row's own hotelName so the column reads as if it were
// authored for this client. Numbers/terms in the description are left intact —
// see the design doc for the accepted residual risk.
const escapeRegex = (s: string): string =>
  s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Names shorter than this are excluded from the match alternation. Junk/test
// accounts often have a 1–2 character "hotel name" (e.g. "t", "k"); included in
// the alternation, a name like "t" matches its letter throughout the copy and
// replaces every "t" in "Autumn creates…" with the full hotel name. Real hotel
// names clear this bar.
const MIN_REWRITABLE_NAME_LENGTH = 3;

export const rewriteHotelNames = (
  description: string,
  ownHotelName: string | null | undefined,
  knownHotelNames: readonly string[],
): string => {
  if (!ownHotelName || !description) return description;

  const cleanedNames = Array.from(
    new Set(
      [...knownHotelNames, ownHotelName]
        .map((n) => (n ?? "").trim())
        .filter((n) => n.length >= MIN_REWRITABLE_NAME_LENGTH),
    ),
  ).sort((a, b) => b.length - a.length);

  if (cleanedNames.length === 0) return description;

  const hotelAlt = `(?:${cleanedNames.map(escapeRegex).join("|")})`;
  const sep = `(?:\\s*,\\s*and\\s+|\\s*,\\s*|\\s+and\\s+|\\s*&\\s*)`;
  // \b at the edges so a name only matches as a whole word/phrase, never as a
  // substring inside a larger word (e.g. "Inn" inside "winning").
  const pattern = new RegExp(`\\b${hotelAlt}(?:${sep}${hotelAlt})*\\b`, "gi");

  return description.replace(pattern, ownHotelName);
};

// =============================================================================
// PLAN LABEL (sourced from OUR billing config, not Stripe)
// =============================================================================

/**
 * Describe a client's billing arrangement from `client_billing_config` — the
 * record that actually decides what they are charged.
 *
 * The Plan column used to read exclusively from Stripe subscriptions, which only
 * ever answers for clients carrying a recurring Stripe product. Commission
 * clients have no subscription at all, and the performance-backed minimum has no
 * Stripe representation whatsoever — there is no product, price or plan behind
 * it, because the floor is applied when we assemble the invoice. Every one of
 * those rows therefore rendered "No plan" while being billed perfectly well.
 * Our own config is the authority; the Stripe description survives only as a
 * fallback for legacy subscription clients whose plan genuinely does live there.
 *
 * Wording deliberately matches `formatEconomicPlan` in the CRM grid
 * (`pages/crm/columns.tsx`) so the same customer is not described two
 * different ways in two places — including its one load-bearing distinction:
 * a floor renders as "or $X min", NEVER "+ $X", because "+" is reserved for a
 * platform fee charged ON TOP of the commission. The term suffix (", M2M" /
 * ", 12mo") is the one part not carried over: contract term is not in this
 * grid's payload.
 *
 * Returns null when nothing is configured, so the caller can fall through.
 */
export const describeBillingPlan = (
  client: Pick<
    ClientBilling,
    | "billingType"
    | "commissionRate"
    | "minimumFeeAmount"
    | "platformFeeAmount"
    | "displayBasePrice"
  >,
): string | null => {
  // commissionRate arrives already scaled to a percentage (0.13 -> 13). Shown
  // to full precision rather than the CRM's rounded whole percent: this is the
  // grid money is charged from, where a 12.5% rate must not read as 13%.
  const rate = Number(client.commissionRate);
  const hasRate = Number.isFinite(rate) && rate > 0;
  const ratePct = hasRate ? formatCommissionRate(rate) : null;
  const money = (n: number) => formatCurrency(n);

  switch (client.billingType) {
    case "commission_or_flat": {
      const min = client.minimumFeeAmount ?? 0;
      if (min <= 0) return hasRate ? `Commission ${ratePct}%` : null;
      // "or … min" — the greater of the two, not a fee stacked on top.
      return hasRate
        ? `Commission ${ratePct}% or ${money(min)} min`
        : `Commission TBD or ${money(min)} min`;
    }
    case "hybrid": {
      const fee = client.platformFeeAmount ?? 0;
      if (fee <= 0) return hasRate ? `Commission ${ratePct}%` : null;
      return hasRate
        ? `Commission ${ratePct}% + ${money(fee)}/mo`
        : `Fixed ${money(fee)}/mo`;
    }
    case "fixed": {
      const flat = client.platformFeeAmount ?? client.displayBasePrice ?? 0;
      return flat > 0 ? `Fixed ${money(flat)}/mo` : null;
    }
    // `commission`, and legacy rows with a rate but no billingType recorded.
    default:
      return hasRate ? `Commission ${ratePct}%` : null;
  }
};

// =============================================================================
// CURRENCY FORMATTING
// =============================================================================

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const formatCurrencyPrecise = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// =============================================================================
// COMMISSION RATE FORMATTING
// =============================================================================

export const formatCommissionRate = (rate: number): string => {
  // Round to 2 decimal places and remove trailing zeros
  const rounded = Math.round(rate * 100) / 100;
  return rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(2);
};

// =============================================================================
// BILLING RATE FORMATTING (hybrid-aware)
// =============================================================================

export function formatBillingRate(client: ClientBilling): string {
  if (client.billingType === "hybrid") {
    const ratePct = ((client.commissionRate || 0) * 100).toFixed(1).replace(/\.0$/, "");
    const fee = client.platformFeeAmount
      ? `$${client.platformFeeAmount.toLocaleString()}`
      : "";
    return `${ratePct}% + ${fee}`;
  }
  if (client.billingType === "fixed") {
    return client.platformFeeAmount
      ? `$${client.platformFeeAmount.toLocaleString()}`
      : "Fixed";
  }
  const ratePct = ((client.commissionRate || 0) * 100).toFixed(1).replace(/\.0$/, "");
  if (client.billingType === "commission_or_flat") {
    // "min" — not "+". The floor is recorded, not added: these clients are
    // charged the commission alone today.
    return client.minimumFeeAmount
      ? `${ratePct}% (min $${client.minimumFeeAmount.toLocaleString()})`
      : `${ratePct}%`;
  }
  return `${ratePct}%`;
}

// =============================================================================
// DATE HELPERS
// =============================================================================

export const generateMonthOptions = (): { value: string; label: string }[] => {
  const options: { value: string; label: string }[] = [];
  // Add next 12 months (future)
  for (let i = 1; i <= 12; i++) {
    const date = moment().add(i, "months");
    options.push({
      value: date.format("YYYY-MM"),
      label: date.format("MMMM YYYY"),
    });
  }
  // Add past 12 months (including current month)
  for (let i = 0; i < 12; i++) {
    const date = moment().subtract(i, "months");
    options.push({
      value: date.format("YYYY-MM"),
      label: date.format("MMMM YYYY"),
    });
  }
  return options;
};

// =============================================================================
// DATA TRANSFORMATION
// =============================================================================

export const transformBillingData = (apiData: any[]): ClientBilling[] => {
  return apiData.map((item: any) => ({
    id: item.chargeId || item.userId,
    accountCreatedAt: item.accountCreatedAt || undefined,
    userId: item.userId,
    clientName: item.clientName,
    hotelName: item.hotelName || item.clientName,
    email: item.userEmail,
    googleAdsCustomerId: item.googleAdsCustomerId || "",
    commissionRate: Math.round((item.commissionRate * 100) * 100) / 100, // Convert 0.14 to 14 (round to 2 decimals)
    bookingRevenue: item.bookingRevenue,
    commission: item.commissionAmount,
    totalAmount: item.totalAmount ?? null,
    invoiceStatus: item.status as InvoiceStatus,
    stripeCustomerId: item.stripeCustomerId || undefined,
    stripeEmail: item.billingEmail || item.userEmail,
    billingName: item.clientName,
    paymentMethod: item.paymentMethod
      ? {
          brand: item.paymentMethod.brand || undefined,
          last4: item.paymentMethod.last4 || undefined,
          expMonth: item.paymentMethod.expMonth || undefined,
          expYear: item.paymentMethod.expYear || undefined,
        }
      : undefined,
    canCharge: item.canCharge,
    warningMessage: item.warningMessage,
    billingType: item.billingType || null,
    minimumFeeAmount: item.minimumFeeAmount != null ? parseFloat(item.minimumFeeAmount) : null,
    minimumFeeMayApply: item.minimumFeeMayApply === true,
    platformFeeAmount: item.platformFeeAmount != null ? parseFloat(item.platformFeeAmount) : null,
    platformFeeDescription: item.platformFeeDescription || null,
    addons: mapAddons(item.addons),
    dashboardEmailMarketingEnabled: item.dashboardEmailMarketingEnabled ?? false,
    displayBasePrice: item.displayBasePrice != null ? parseFloat(item.displayBasePrice) : null,
    displayCommissionMarkup: item.displayCommissionMarkup != null ? parseFloat(item.displayCommissionMarkup) : null,
    showOnboardingFee: item.showOnboardingFee ?? null,
    showReputationManagementWaiver:
      item.showReputationManagementWaiver ?? null,
    planDescription: item.planDescription || null,
    enrichmentPending: item.enrichmentPending ?? false,
    isTestUser: item.isTestUser ?? false,
    isManualOverride: item.isManualOverride ?? false,
    isCustomAmount: item.isCustomAmount ?? false,
    calculatedAmount: item.calculatedAmount ?? null,
    overrideReason: item.overrideReason ?? null,
    createdByName: item.createdByName ?? null,
    chargedAt: item.chargedAt ?? null,
    collectionMethod: item.collectionMethod ?? null,
    invoicedAt: item.invoicedAt ?? null,
    invoiceDueAt: item.invoiceDueAt ?? null,
    stripeHostedInvoiceUrl: item.stripeHostedInvoiceUrl ?? null,
    rechargeCount: item.rechargeCount ?? 0,
    lastRechargedAt: item.lastRechargedAt ?? null,
    lastRechargedByName: item.lastRechargedByName ?? null,
    resetCount: item.resetCount ?? 0,
    lastResetAt: item.lastResetAt ?? null,
    lastResetByName: item.lastResetByName ?? null,
  }));
};

export const mergeEnrichmentData = (
  tableData: ClientBilling[],
  enrichmentData: Array<{
    userId: string;
    paymentMethod: any;
    bookingRevenue: number | null;
    commissionAmount: number | null;
    canCharge: boolean;
    warningMessage: string | null;
  }>,
): ClientBilling[] => {
  const enrichMap = new Map(enrichmentData.map(e => [e.userId, e]));
  return tableData.map(client => {
    const enrichment = enrichMap.get(client.userId);
    if (!enrichment) return client;
    return {
      ...client,
      paymentMethod: enrichment.paymentMethod
        ? {
            brand: enrichment.paymentMethod.brand || undefined,
            last4: enrichment.paymentMethod.last4 || undefined,
            expMonth: enrichment.paymentMethod.expMonth || undefined,
            expYear: enrichment.paymentMethod.expYear || undefined,
          }
        : client.paymentMethod,
      bookingRevenue: enrichment.bookingRevenue ?? client.bookingRevenue,
      commission: enrichment.commissionAmount ?? client.commission,
      canCharge: enrichment.canCharge,
      warningMessage: enrichment.warningMessage || undefined,
      enrichmentPending: false,
    };
  });
};

/** A row with a rate but no charge record yet is "pending", not "not configured". */
export const getEffectiveStatus = (client: ClientBilling): InvoiceStatus =>
  client.invoiceStatus === "not_configured" && client.commissionRate > 0
    ? "pending"
    : client.invoiceStatus;

// Calculate a data completeness score (higher = more complete)
const getCompletenessScore = (item: ClientBilling): number => {
  let score = 0;
  if (item.planDescription) score += 4;   // Has plan (highest weight)
  if (item.stripeCustomerId) score += 2;  // Has Stripe linked
  if (item.paymentMethod) score += 2;     // Has payment method
  if (item.commissionRate > 0) score += 1; // Has rate configured
  if (item.googleAdsCustomerId) score += 1; // Has Google Ads
  return score;
};

export const sortBillingData = (data: ClientBilling[]): ClientBilling[] => {
  return [...data].sort((a, b) => {
    // Priority 1: Status (pending first, not_configured last)
    const aStatus = STATUS_PRIORITY[getEffectiveStatus(a)] ?? 99;
    const bStatus = STATUS_PRIORITY[getEffectiveStatus(b)] ?? 99;
    if (aStatus !== bStatus) return aStatus - bStatus;

    // Priority 2: Has plan (users with plans first)
    const aHasPlan = a.planDescription ? 0 : 1;
    const bHasPlan = b.planDescription ? 0 : 1;
    if (aHasPlan !== bHasPlan) return aHasPlan - bHasPlan;

    // Priority 3: canCharge = true (fully configured)
    const aCanCharge = a.canCharge ? 0 : 1;
    const bCanCharge = b.canCharge ? 0 : 1;
    if (aCanCharge !== bCanCharge) return aCanCharge - bCanCharge;

    // Priority 4: Data completeness (more complete data first)
    const aScore = getCompletenessScore(a);
    const bScore = getCompletenessScore(b);
    if (aScore !== bScore) return bScore - aScore;

    // Finally: alphabetically
    return a.clientName.localeCompare(b.clientName);
  });
};

// =============================================================================
// CSV EXPORT
// =============================================================================

const escapeCsvField = (value: string): string => {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

export const exportBillingCsv = (data: ClientBilling[]): void => {
  // Matches table column order: Client, Plan, Hotel Name, Rate, Status
  // Plus extra detail columns: Stripe Email, Stripe Customer ID, Billing Type, etc.
  const headers = [
    "Contact Name",
    "Auth Email",
    "Stripe Email",
    "Autumn Product",
    "Hotel Name",
    "Stripe Customer ID",
    "Commission Rate",
    "Status",
    "Subscription Status",
    "Billing Type",
    "Minimum Fee",
    "Google Ads Customer ID",
    "Display Base Price",
    "Display Commission Markup",
    "Show Onboarding Fee",
  ];

  const knownHotelNames = data
    .map((c) => c.hotelName)
    .filter((n): n is string => Boolean(n));

  const rows = data.map((client) => [
    escapeCsvField(client.clientName || ""),
    escapeCsvField(client.email || ""),
    escapeCsvField(client.stripeEmail || ""),
    escapeCsvField(
      rewriteHotelNames(
        client.planDescription || "",
        client.hotelName,
        knownHotelNames,
      ),
    ),
    escapeCsvField(client.hotelName || ""),
    escapeCsvField(client.stripeCustomerId || ""),
    `${formatCommissionRate(client.commissionRate)}%`,
    escapeCsvField(client.invoiceStatus || ""),
    escapeCsvField(client.subscriptionStatus || ""),
    escapeCsvField(client.billingType || ""),
    client.minimumFeeAmount != null ? formatCurrencyPrecise(client.minimumFeeAmount) : "",
    escapeCsvField(client.googleAdsCustomerId || ""),
    client.displayBasePrice != null ? formatCurrencyPrecise(client.displayBasePrice) : "",
    client.displayCommissionMarkup != null ? `${(client.displayCommissionMarkup * 100).toFixed(1)}%` : "",
    client.showOnboardingFee != null ? (client.showOnboardingFee ? "Yes" : "No") : "",
  ]);

  const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `billing-export-${moment().format("YYYY-MM-DD")}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

export const fetchAllBillingData = async (
  month: string,
  getBillingList: (month: string, page?: number, limit?: number) => Promise<any>,
): Promise<any[]> => {
  const allData: any[] = [];
  let page = 1;
  const limit = 200;
  // First call to get total count
  const firstRes = await getBillingList(month, page, limit);
  allData.push(...firstRes.data);
  const total = firstRes.total ?? firstRes.data.length;
  page++;
  // Fetch remaining pages
  while (allData.length < total) {
    const res = await getBillingList(month, page, limit);
    if (!res.data || res.data.length === 0) break;
    allData.push(...res.data);
    page++;
  }
  return allData;
};

// =============================================================================
// PREVIEW DATA TRANSFORMATION
// =============================================================================

export const transformPreviewData = (
  previewData: any,
  originalClient: ClientBilling
): ClientBilling => {
  return {
    id: originalClient.id,
    userId: previewData.userId,
    clientName: previewData.clientName,
    hotelName: originalClient.hotelName,
    email: previewData.userEmail,
    googleAdsCustomerId: originalClient.googleAdsCustomerId,
    commissionRate: Math.round((previewData.commissionRate * 100) * 100) / 100, // Convert 0.14 to 14 (round to 2 decimals)
    bookingRevenue: previewData.bookingRevenue,
    commission: previewData.commissionAmount,
    invoiceStatus: originalClient.invoiceStatus,
    stripeCustomerId: previewData.stripeCustomerId || undefined,
    stripeEmail: previewData.billingEmail || previewData.userEmail || undefined,
    billingName: previewData.clientName,
    billingAddress: previewData.billingAddress || undefined,
    paymentMethod: previewData.paymentMethod || undefined,
    canCharge: previewData.canCharge,
    warningMessage: previewData.warningMessage || undefined,
    planRateMismatch: previewData.planRateMismatch ?? false,
    planExpectedRate: previewData.planExpectedRate ?? null,
    planRateStatus: previewData.planRateStatus ?? undefined,
    billingType: originalClient.billingType,
    minimumFeeAmount: originalClient.minimumFeeAmount,
    minimumFeeMayApply: originalClient.minimumFeeMayApply,
    platformFeeAmount: originalClient.platformFeeAmount,
    platformFeeDescription: originalClient.platformFeeDescription,
    displayBasePrice: originalClient.displayBasePrice,
    displayCommissionMarkup: originalClient.displayCommissionMarkup,
    showOnboardingFee: originalClient.showOnboardingFee,
    showReputationManagementWaiver:
      originalClient.showReputationManagementWaiver,
    planDescription: originalClient.planDescription,
    totalAmount: originalClient.totalAmount,
    // Server-authoritative total: commission (+ platformFee) + add-on === sum(lineItems).
    // Seed the modal total from this instead of re-deriving client-side, which omits the add-on.
    totalChargeAmount:
      previewData.totalChargeAmount != null
        ? Number(previewData.totalChargeAmount)
        : null,
    addons: originalClient.addons,
    dashboardEmailMarketingEnabled:
      originalClient.dashboardEmailMarketingEnabled,
    lineItems: previewData.lineItems || [],
    // Emailed-invoice billing: the customer's standing preference (preselects
    // the notice option) and the date they were already promised, when this
    // period is booked. Both are preview-only — the list row has neither.
    prefersEmailInvoice: previewData.prefersEmailInvoice ?? false,
    scheduledChargeOn: previewData.scheduledChargeOn ?? null,
    // Audit fields are properties of the existing charge record — forward them.
    isManualOverride: originalClient.isManualOverride,
    isCustomAmount: originalClient.isCustomAmount,
    calculatedAmount: originalClient.calculatedAmount,
    overrideReason: originalClient.overrideReason,
    createdByName: originalClient.createdByName,
    chargedAt: originalClient.chargedAt,
    collectionMethod: originalClient.collectionMethod,
    invoicedAt: originalClient.invoicedAt,
    invoiceDueAt: originalClient.invoiceDueAt,
    stripeHostedInvoiceUrl: originalClient.stripeHostedInvoiceUrl,
    rechargeCount: originalClient.rechargeCount,
    lastRechargedAt: originalClient.lastRechargedAt,
    lastRechargedByName: originalClient.lastRechargedByName,
    resetCount: originalClient.resetCount,
    lastResetAt: originalClient.lastResetAt,
    lastResetByName: originalClient.lastResetByName,
  };
};

// =============================================================================
// TABLE DERIVATIONS (pure — consumed by Billing.tsx)
// =============================================================================

const matchesSearch = (client: ClientBilling, query: string) =>
  client.hotelName?.toLowerCase().includes(query) ||
  client.clientName?.toLowerCase().includes(query) ||
  client.email?.toLowerCase().includes(query) ||
  client.stripeEmail?.toLowerCase().includes(query);

export const filterClients = (
  data: ClientBilling[],
  f: BillingFilters,
): ClientBilling[] => {
  const query = f.searchQuery.toLowerCase();
  return data.filter((client) => {
    if (client.isTestUser && !f.showTestUsers) return false;
    if (query && !matchesSearch(client, query)) return false;
    if (f.statusFilter !== "all" && getEffectiveStatus(client) !== f.statusFilter)
      return false;
    if (
      f.showReadyOnly &&
      (!client.canCharge ||
        (!isChargeableWithoutCommission(client) && client.commissionRate === 0))
    )
      return false;
    return true;
  });
};

/** Tile numbers. Counts follow the Show Test Accounts toggle; money totals
 *  prefer the backend summary (enrichment-independent) and fall back to row sums. */
export const computeSummaryStats = (
  tableData: ClientBilling[],
  summary: { totalBookingRevenue?: number; totalCommission?: number } | null | undefined,
  showTestUsers: boolean,
): SummaryStats => {
  const real = showTestUsers ? tableData : tableData.filter((c) => !c.isTestUser);
  const count = (status: InvoiceStatus) =>
    real.filter((c) => getEffectiveStatus(c) === status).length;
  return {
    totalClients: real.length,
    totalRevenue:
      summary?.totalBookingRevenue ??
      real.reduce((sum, c) => sum + c.bookingRevenue, 0),
    totalCommission:
      summary?.totalCommission ??
      real.reduce((sum, c) => sum + c.commission, 0),
    pendingInvoices: count("pending"),
    awaitingPaymentInvoices: count("invoiced"),
    paidInvoices: count("paid"),
  };
};

/** Overlay Stripe plan info by userId, falling back to stripeCustomerId. */
export const mergePlanInfo = (
  clients: ClientBilling[],
  map: PlanInfoMap,
): ClientBilling[] =>
  clients.map((client) => {
    const info =
      map.userIdMap.get(client.userId) ||
      (client.stripeCustomerId
        ? map.stripeIdMap.get(client.stripeCustomerId)
        : undefined);
    if (!info) return client;
    return {
      ...client,
      ...(info.planDescription && { planDescription: info.planDescription }),
      subscriptionStatus: info.subscriptionStatus,
    };
  });
